import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardText, CheckSquare, Camera, Note, BellRinging, ArrowRight, Microphone,
} from '@phosphor-icons/react';
import VoiceTaskSplitMock from './VoiceTaskSplitMock';

/**
 * "Daily Checklists" landing promo. Prominent, role-aware feature block for
 * HowItWorks / role funnel pages. Copy shifts per role.
 *
 * Props:
 *   role : "contractor" | "worker"   (defaults to a generic pitch)
 */
const DailyChecklistPromo = ({ role = 'contractor' }) => {
  const navigate = useNavigate();
  const isWorker = role === 'worker';

  const copy = isWorker
    ? {
        eyebrow: 'For Workers · Field-First',
        headline: "Know exactly what to do today.",
        subhead:
          "Your contractor drops today's tasks straight to your phone. Tap-to-check each one as you finish. Add a photo + note when it matters.",
        cta: 'Start Getting Work',
        ctaRoute: '/signup?role=worker',
      }
    : {
        eyebrow: 'For Contractors · Command Your Crew',
        headline: 'Run your crew like a general contractor.',
        subhead:
          "Push per-worker daily checklists to each job. Get pinged the moment a task is checked off — with an optional photo proof and note attached. Zero group chats. Zero excuses.",
        cta: 'Sign Up Free',
        ctaRoute: '/signup?role=contractor',
      };

  const bullets = isWorker
    ? [
        { Icon: CheckSquare, text: 'Tap-to-check tasks on the jobsite' },
        { Icon: Camera, text: 'Attach photo proof (up to 6)' },
        { Icon: Microphone, text: 'Dictate notes hands-free — EN or ES' },
        { Icon: BellRinging, text: 'Contractor sees it live — no chasing' },
      ]
    : [
        { Icon: Microphone, text: 'Dictate tasks by voice — AI splits them (EN/ES)' },
        { Icon: ClipboardText, text: 'Assign per-worker, per-day, per-job' },
        { Icon: Camera, text: 'Photo proof + notes attached to each task' },
        { Icon: BellRinging, text: 'Real-time push when tasks close' },
      ];

  return (
    <section
      className="relative py-14 md:py-20 border-y-4 border-black bg-[#E8E9EB] dark:bg-[#121212]"
      data-testid={`checklist-promo-${role}`}
    >
      <div className="max-w-6xl mx-auto px-4">
        {/* Eyebrow ribbon */}
        <div className="inline-flex items-center gap-2 bg-black text-[#D95800] text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-[#D95800] rounded mb-4 shadow-[3px_3px_0px_#D95800]">
          <ClipboardText size={14} weight="fill" /> {copy.eyebrow} · New
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
          {/* Copy column */}
          <div className="lg:col-span-3">
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-tight mb-3">
              {copy.headline}
            </h2>
            <p className="text-base md:text-lg text-neutral-700 dark:text-neutral-300 mb-6 max-w-xl">
              {copy.subhead}
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {bullets.map(({ Icon, text }, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 bg-white dark:bg-neutral-900 border-4 border-black p-3 shadow-[4px_4px_0px_#000]"
                  data-testid={`checklist-promo-bullet-${i}`}
                >
                  <div className="w-9 h-9 flex-shrink-0 bg-[#D95800] border-2 border-black flex items-center justify-center">
                    <Icon size={18} weight="bold" className="text-black" />
                  </div>
                  <span className="text-sm font-bold leading-snug text-neutral-800 dark:text-neutral-200">
                    {text}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => navigate(copy.ctaRoute)}
              className="inline-flex items-center gap-2 h-12 px-6 bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase text-sm tracking-widest border-4 border-black shadow-[5px_5px_0px_#000] hover:shadow-[6px_6px_0px_#000] active:translate-y-0.5 active:shadow-[3px_3px_0px_#000] rounded-none transition-all"
              data-testid={`checklist-promo-cta-${role}`}
            >
              {copy.cta}
              <ArrowRight size={16} weight="bold" />
            </button>
          </div>

          {/* Visual mockup column */}
          <div className="lg:col-span-2">
            <div className="bg-black border-4 border-[#D95800] shadow-[8px_8px_0px_#D95800] p-4 md:p-5">
              {isWorker ? (
                /* Faux "phone" mockup */
                <div className="bg-white dark:bg-neutral-900 border-2 border-black p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#D95800]">
                      <ClipboardText size={12} weight="fill" /> Today&apos;s Tasks
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest bg-[#D95800] text-white px-1.5 py-0.5 border-2 border-black">1 List</span>
                  </div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-[#D95800] mb-1">Kitchen Remodel</div>
                  <div className="font-black text-base mb-2">Framing Crew — Morning</div>
                  <div className="h-2 bg-neutral-200 dark:bg-neutral-700 border border-black overflow-hidden mb-3">
                    <div className="h-full bg-emerald-500" style={{ width: '66%' }} />
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    <MockTask done text="Frame the west wall" />
                    <MockTask done text="Install drywall" note photos={1} />
                    <MockTask text="Cleanup site" />
                  </ul>
                </div>
              ) : (
                /* Animated mic → task-split demo */
                <VoiceTaskSplitMock />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MockTask = ({ text, done = false, note = false, photos = 0 }) => (
  <li className={`flex items-start gap-2 p-2 border-2 ${done ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500' : 'bg-white dark:bg-neutral-900 border-black'}`}>
    <span className={`flex-shrink-0 w-4 h-4 mt-0.5 border-2 border-black flex items-center justify-center ${done ? 'bg-emerald-500' : 'bg-white dark:bg-neutral-800'}`}>
      {done && <CheckSquare size={10} weight="bold" className="text-white" />}
    </span>
    <div className="flex-1 min-w-0">
      <div className={`text-xs leading-snug ${done ? 'line-through text-neutral-400' : 'text-neutral-800 dark:text-neutral-200'}`}>{text}</div>
      {(note || photos > 0) && (
        <div className="flex items-center gap-1 mt-1">
          {photos > 0 && (
            <span className="inline-flex items-center gap-1 text-[8px] font-black uppercase tracking-widest bg-black text-white px-1 py-0.5">
              <Camera size={8} weight="fill" /> {photos}
            </span>
          )}
          {note && (
            <span className="inline-flex items-center gap-1 text-[8px] font-black uppercase tracking-widest bg-neutral-200 dark:bg-neutral-700 dark:text-white px-1 py-0.5 border border-black">
              <Note size={8} weight="fill" /> Note
            </span>
          )}
        </div>
      )}
    </div>
  </li>
);

export default DailyChecklistPromo;
